<script setup lang="ts">
import { useGenerateImageVariant } from "@/@core/composable/useGenerateImageVariant";
import OAuthSignupDialog from "@/components/users/OAuthSignupDialog.vue";
import TwoFactorAuthDialog from "@/components/users/TwoFactorAuthDialog.vue";
import { useGlobalSetupStore } from "@/utils/GlobalSetupStore";
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";
import { useUserRole } from "@/utils/useUserRole";
import AuthProvider from "@/views/authentication/AuthProvider.vue";
import authV1LoginMaskDark from "@images/pages/auth-v1-login-mask-dark.png";
import authV1LoginMaskLight from "@images/pages/auth-v1-login-mask-light.png";
import { requiredValidator } from "@validators";
import isEmpty from "lodash/isEmpty";
import { toast } from "vue3-toastify";
import { VForm } from "vuetify/components/VForm";
import ImportedTwoFactorAuthDialog from "@/components/users/ImportedTwoFactorAuthDialog.vue";

// stores
const userStore = useUserStore();
const userRoleStore = useUserRole();
const globalStore = useGlobalSetupStore();


// NOTES:
// This will check the first part of the domain - upto the first "."
// ADD more subdomains here to check
// Comment out subdomains to remove
const domainForOldLogin = ref(
  [
    {siteLookup:'foreseasons', oldLoginLink:'https://foreseasons.helloclub.com/login/credentials'},
    {siteLookup:'localhost', oldLoginLink:'https://foreseasons.helloclub.com/login/credentials'},
    {siteLookup:'data', oldLoginLink:'https://foreseasons.helloclub.com/login/credentials'}
  ],
)

const { getUserRolePermission } = userRoleStore;

// Open - we need your card details message

const { globalSetup } = storeToRefs(globalStore);
const { currentLocation, userId } = storeToRefs(userRoleStore);

const authV1ThemeLoginMask = useGenerateImageVariant(
  authV1LoginMaskLight,
  authV1LoginMaskDark
);

const directDebitEnabled = ref(false)
const CanDoDirectDebit = async () => {
  // Get Users Location
  try {
    let userValues = await pb.collection("users").getOne(importedID.value)

    if (userValues && userValues.homeLocation) {
      let location = await pb.collection("locations").getOne(userValues.homeLocation)
      if(location) {
        directDebitEnabled.value = location?.onlineDirectDebitEnabled ? true : false
      }
    }
  } catch(e){

  }
  return false;
}

// Data
const isOAuthSignupDialog = ref(false);
const userToAuth = ref<any>();
const form = ref<VForm>();
const email = ref("");
const password = ref("");
const importedID = ref("");
const rememberMe = ref(false);
const isTwoFADialogOpen = ref(false);
const isImportedTwoFADialogOpen = ref(false);
const errors = ref<Record<string, string | undefined>>({
  email: undefined,
  password: undefined,
});
const isPasswordVisible = ref(false);
const isErrorPopupVisible = ref<boolean>(false);

// Computed
const getSignInLogo = computed(() => {
  return `${pb.baseUrl}/api/files/${globalSetup.value?.collectionId}/${globalSetup.value?.id}/${globalSetup.value?.signInLogo}`;
});

const getBackGroundImage = computed(() => {
  return (
    `${pb.baseUrl}/api/files/${globalSetup.value?.collectionId}/${globalSetup.value?.id}/${globalSetup.value?.backGroundImage}` ||
    authV1ThemeLoginMask
  );
});

// Router
const router = useRouter();

const oldLogin = ref(false)
/**
 * Create Imported User with 2fa
 * three steps
 * 1.) Update the users password to the entered value
 * 2.) Set the user status to Validating --
 * 3.) Take the user to the membership page to re-enter CC details
 * 4.) If Email does not exist - Ask them to go to Hello Club
 */
const checkImportedUser = async () => {
  if(password.value.trim() != '' && password.value.trim().length > 5) {
    try {
      // Actually - lets not log the user in yet...
      const response = await pb
        .collection("users")
        .authWithPassword(email.value.toLowerCase(), "!qaz2wsX");

      if (response && response.record && response.record.id) {
        pb.authStore.clear()        // Not allowed in Just Yet!
        localStorage.removeItem('pocketbase_auth')
        errors.value.email = ''    // VALID LOGIN - Let's Hide the Error Message
        let newDetails = {
          //oldPassword:"!qaz2wsX",
          //password:password.value.trim(),
          //passwordConfirm:password.value.trim(),
          emailVisibility:true,
          twoFACode:'',
          status:'ImpValidating'     // ImpVerifying Does not Send An Email - This is the preferred State
        }
        importedID.value = response.record.id

        await CanDoDirectDebit();
        // UPDATE USER
        await pb.collection("users").update(response.record.id,newDetails)
        email.value = response.record.email
        isImportedTwoFADialogOpen.value = true;
      } else {
        // Check if Email Exists - If Not Ask them to go to Former Login
        errors.value.email = "Invalid username or password";
      }
    } catch (e) {
      try{
        let doesUserExist = await pb.collection("users").getFirstListItem('email="' + email.value + '"')
        if(doesUserExist){
          errors.value.email = "Invalid username or password";
        }
      } catch(e){
         // check login
        if(domainForOldLogin.value && domainForOldLogin.value.length > 0){
          let link = '';
          let parts = window.location.href.toString().trim().toLowerCase().split('.')[0]
          domainForOldLogin.value.forEach((dc:any) => {
            if(parts.indexOf(dc.siteLookup.toString().trim().toLowerCase()) != -1){
              oldLogin.value = true
              link = dc.oldLoginLink
            }
          })
        }
        errors.value.email = "Invalid username or password";
      }
    }
  }
  errors.value.email = "Invalid username or password";
};

const transitionMembers = ref(false)

const getOldLoginLink = () => {
  let link = '';
  if(domainForOldLogin.value && domainForOldLogin.value.length > 0 && transitionMembers.value){
    let parts = window.location.href.toString().trim().toLowerCase().split('.')[0]
    domainForOldLogin.value.forEach((dc:any) => {
      if(parts.indexOf(dc.siteLookup.toString().trim().toLowerCase()) != -1){
        link = dc.oldLoginLink
      }
    })
  }
  return link;
}


const usersCreditCards:Ref<any> = ref([])
const getCreditCards = async () => {
  try {
    // Fetch all records from the 'bankCards' collection in the database,
    // sorted by creation date in descending order,
    // and filtered to only include credit cards associated with the current user.
    const records = await pb.collection("bankCards").getFullList({
      sort: "-isPrimary",
      filter: `user = '${importedID.value}'`,
    });

    // Assign the fetched credit cards to the 'usersCreditCards' reactive property.
    if (records) usersCreditCards.value = records;
    else usersCreditCards.value = [];
  } catch (error) {
    // Log any errors that occur during the fetching process.
    console.log(`Error getting Credit Cards: ${error}`);
  }
};


const makeScrambePass = (length:number) => {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
}

const validatePostLoginData = async (response: any) => {
  try {
    const userData = response.record;
    if ((response.token &&
      !response.record.twoFA &&
      !response.record.twoFACode) || response?.record?.status  == "ValidatingMobile"
    ) {

      importedID.value = userData.id

      const status = response?.record?.status;
      if (status === "Suspended") {
        isErrorPopupVisible.value = true;
        return;
      }

      if(status == "ImpValidating"){
        email.value = pb.authStore?.model?.email
        pb.authStore.clear()        // Not allowed in Just Yet!
        localStorage.removeItem('pocketbase_auth')
        isImportedTwoFADialogOpen.value = true;
        return
      }

      if(response?.record?.status == "ImpCompleted" && response?.record?.renewMembership != '') {
        importedID.value = response?.record?.id
        try {
          let membershipCostCheck = await pb.collection("memberships").getOne(response.record.renewMembership)
          if (membershipCostCheck && membershipCostCheck.recouringFee && membershipCostCheck.recouringFee > 0) {
            await CanDoDirectDebit();

            let records = await pb.collection("bankCards").getFullList({
              sort: "-isPrimary",
              filter: `user = '${importedID.value}'`,
            });

            // Assign the fetched credit cards to the 'usersCreditCards' reactive property.
            if (records && records.length > 0) {
              // We Have Cards for the user - ignore
            } else {
              verifyCC.value = true
              return
            }
          }
        } catch (e) {}
      }

      if (status === "Inactive") {
        userData.status = "Validating";
        userData.emailVisibility = true
        await pb.collection("users").update(userData.id, userData);
        isTwoFADialogOpen.value = true;
        return;
      }

      // AUTH WITH GOOGLE METHOD
      if (status === "Imported" && response?.record?.membership != '' && isSocialAuth.value) {
        let records = await pb.collection("bankCards").getFullList({
          sort: "-isPrimary",
          filter: `user = '${response?.record?.id}'`,
        });


        let d = new Date()
        let newPass = makeScrambePass(7) + "&6!" + d.getTime()
        // Scramble Password for Security Reasons
        userData.emailVisibility = true;
        userData.status = "ImpCompleted"
        userData.oldPassword = "!qaz2wsX"
        userData.password = newPass
        userData.passwordConfirm = newPass

        // Update the user's record in the database
        try {
          await pb.collection("users").update(response?.record?.id, userData);
        } catch(e){
          // USer may have already changed their password
        }

        // Assign the fetched credit cards to the 'usersCreditCards' reactive property.
        if (records && records.length > 0){
          // We Have Cards for the user - ignore
        } else {
          try {
            let membershipCostCheck = await pb.collection("memberships").getOne(response.record.renewMembership)
            if (membershipCostCheck && membershipCostCheck.recouringFee && membershipCostCheck.recouringFee > 0) {
              verifyCC.value = true
              return
            }
          } catch(e){}
        }
      }

      if (status === "Imported" && !isSocialAuth.value) {
        email.value = pb.authStore?.model?.email
        pb.authStore.clear()        // Not allowed in Just Yet!
        localStorage.removeItem('pocketbase_auth')
        isImportedTwoFADialogOpen.value = true;
        return;
      }

      userData.status = "Active";
      userData.lastLogin = moment().toISOString();
      userData.emailVisibility = true;

      try {
        await pb.collection("users").update(userData.id, userData);
      } catch(e){
        let backupData:any = {}
        backupData.status = "Active";
        backupData.lastLogin = moment().toISOString();
        backupData.emailVisibility = true;
        try{
          await pb.collection("users").update(userData.id, backupData);
        }catch(e){}
      }
      localStorage.setItem("email", response.record?.email);
      currentLocation.value = response.record?.homeLocation;
      userStore.currentLocation = response.record?.homeLocation;

      await getUserRolePermission();

      // user requested before some route move them to same path
      let userOnWayTo = JSON.parse(
        sessionStorage.getItem("userOnWayTo") || "{}"
      );

      userId.value = response?.record?.id;

      sessionStorage.setItem("userMode", JSON.stringify(false));

      userMobileNumber.value = userData?.mobileNumber || ''
      userMobileId.value = userData.id
      if((!userData.mobileValidated && globalSetup.value?.validateMobileNumbers) || (userData.mobileNumber == '' && globalSetup.value?.requireMembershipMobile) || response?.record?.status  == "ValidatingMobile"){
        verifyMobile.value = true
        return
      }

      if((!userData?.firstName || userData?.firstName == "") || (!userData?.lastName || userData?.lastName == "")){
        await router.push("/user/" + userData.id);
        return
      }

      if (isEmpty(userOnWayTo) || userOnWayTo != "{}") {
        await router.push("/");
      } else {
        const { name, path, query, params } = userOnWayTo;
        const navigationParams = {
          path,
          query,
          params: name === "user-id" ? { id: userData.id } : params,
        };
        try {
          await router.push(navigationParams);
        } catch(e){
          await router.push("/");
        }
      }
    } else {
      // save user id in local
      const userSignUpData = JSON.parse(
        localStorage.getItem("userSignUpData") || "{}"
      );
      if (userSignUpData) {
        userSignUpData.id = response.record.id;
        userSignUpData.email = response.record.email;
        userSignUpData.username = response.record.username;
        userSignUpData.firstName = response.record.firstName;
        userSignUpData.lastName = response.record.lastName;
        localStorage.setItem("userSignUpData", JSON.stringify(userSignUpData));
      }
      // user don't have verified account. move them to signup page to reverify
      // show error toaster
      toast.success("Please authorize your account first", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      // logout user as they didn't verify their account yet
      // pb.authStore.clear();
      localStorage.removeItem("homeLocation");

      if(userData.status == "ImpValidating"){
        importedID.value = userData.id
        userData.status = "ImpValidating";
        userData.emailVisibility = true
        userData.twoFACode = '';
        email.value = userData?.email || email.value
        await pb.collection("users").update(userData.id, userData);
        isImportedTwoFADialogOpen.value = true;
      } else {
        userData.status = "Validating";
        userData.emailVisibility = true
        email.value = userData?.email || email.value
        await pb.collection("users").update(userData.id, userData);
        isTwoFADialogOpen.value = true;
      }
      return;
    }
  } catch (e) {}
};

const login = async () => {
  isSocialAuth.value = false
  try {
    const response = await pb
      .collection("users")
      .authWithPassword(email.value.toLowerCase(), password.value);
    // verified user goes to the home screen

    await validatePostLoginData(response);
  } catch (e) {
    await checkImportedUser();
  }
};

const loginLoading = ref(false)
const onSubmit = async () => {
  loginLoading.value = true
  try {
    const result = await form.value?.validate();
    if (result?.valid) {
      await login();
      loginLoading.value = false
    } else {
      loginLoading.value = false
    }
  } catch (e: any) {
    loginLoading.value = false
    console.error(e?.response);
  }

};

/**
 * Handles the social authentication process.
 *
 * @param {string} provider - The name of the social authentication provider (e.g., 'google', 'facebook').
 *
 * @returns {Promise<void>}
 */

const isSocialAuth = ref(false)
const handleSocialAuth = async (provider: string) => {
  isSocialAuth.value = true
  try {
    // Authenticate the user with the specified social provider
    const authData = await pb
      .collection("users")
      .authWithOAuth2({ provider: provider });

    // If the user's home location, first name, and last name are not set,
    // open the OAuth signup dialog and pass the user's record to it
    if (authData && (
      (!authData?.record?.homeLocation || authData.record.homeLocation == '')
      ||
      (!authData?.record?.firstName || authData.record.firstName == '')
      ||
      (!authData?.record?.lastName || authData.record.lastName == ''))) {
      try {
      let userData = {
        status: "Active",
        lastLogin: moment().toISOString(),
        emailVisibility:true
      }
        await pb.collection("users").update(authData.record.id, userData);
      } catch(e){

      }
      isOAuthSignupDialog.value = true;
      userToAuth.value = authData?.record;
    } else {
      await validatePostLoginData(authData);
    }
  } catch (e) {}
};

/**
 * Handles the OAuth signup process after a user has authenticated with a social provider.
 *
 * @param {Object} data - The user data to be updated.
 * @param {string} data.id - The ID of the user to be updated.
 * @param {string} data.homeLocation - The user's home location.
 * @param {string} data.firstName - The user's first name.
 * @param {string} data.lastName - The user's last name.
 *
 * @returns {Promise<void>}
 */
const handleOAuthSignUp = async (data: any) => {
  try {
    const response = await pb
      .collection("users")
      .update(`${userToAuth.value?.id}`, data);

    // If the update is successful, update the user's home location, store it in local storage,
    // fetch the user's role permissions, and navigate to the user's profile page.
    if (response) {
      const { homeLocation = "" } = response;
      currentLocation.value = homeLocation;
      localStorage.setItem("homeLocation", homeLocation);
      await getUserRolePermission();
      router.push({
        name: "user-id",
        params: { id: response?.id },
      });
      isOAuthSignupDialog.value = false;
    }
  } catch (error) {
    // If an error occurs during the update process, log the error message.
    console.log("Error setting OAuthSignUp: ", error);
  }
};

const clearLocalStorage = () => {
  localStorage.removeItem("email");
  localStorage.removeItem("homeLocation");
  localStorage.removeItem("userSignUpData");
  localStorage.removeItem("isVerificationCodeSent");
};


onMounted(async () => {
  transitionMembers.value = globalSetup?.value?.transitionMembers
});


// ADD CARD WORKING
const verifyCC = ref(false)

const enterDirectDebit = () => {
  // TODO - DIRECT DEBIT WORKING
  verifyCC.value = false
}

const closeVerifyCC = async () =>{
  // Update User
  let userData = {}
  userData.status = "Active";
  userData.lastLogin = moment().toISOString();
  await pb.collection("users").update(importedID.value, userData);
  let location = window.location.href; // Hard reload the current page
  window.location.href = location.toString().replace("/login", "/user/" + importedID.value)
}

const addCardDialogMessage = ref({
  isVisible: false,
  message: "",
  color: "",
  spinner: false,
});

const isAddCardDialogVisible = ref(false);
const handleAddCard = async (): Promise<void> => {

  let userData = {}
  userData.status = "Active";
  userData.lastLogin = moment().toISOString();
  await pb.collection("users").update(importedID.value, userData);
  const showAddCardDialog = (
    message: string,
    color = "primary",
    spinner = true
  ) => {
    addCardDialogMessage.value = {
      isVisible: true,
      message,
      color,
      spinner,
    };
  };

  isAddCardDialogVisible.value = true;
  showAddCardDialog("Adding card...");

  try {
    const updatedUser = await pb.collection("users").update(`${importedID.value}`, {
      addCreditCard: true,
      addCardWebRedirect: "",
      addCardTransactionReff: "",
      webResponseLoc: window.location.origin,
    });

    if (updatedUser) {
      let elapsedTime = 0;
      const pollInterval = 1000;
      const maxTime = 5000;

      const poll = setInterval(async () => {
        elapsedTime += pollInterval;

        if (!isAddCardDialogVisible.value) {
          // Stop polling if the dialog is closed
          clearInterval(poll);

          return;
        }

        try {
          const userRecord = await pb
            .collection("users")
            .getOne(`${importedID.value}`);

          if (userRecord?.addCardWebRedirect) {
            window.location.href = userRecord.addCardWebRedirect;
            clearInterval(poll);
          } else if (elapsedTime >= maxTime) {
            showAddCardDialog(
              "Cannot add card, Server not responding.",
              "error",
              false
            );
          }
        } catch (pollError) {
          clearInterval(poll);
        }
      }, pollInterval);
    }
  } catch (error) {
    console.error("Error adding card: ", error);
    showAddCardDialog("Error adding card. Please try again.", "error", false);
  }
};

const closeAddCardDialog = async (): Promise<void> => {
  try {
    await pb.collection("users").update(`${importedID.value}`, {
      addCardWebRedirect: "",
      addCreditCard: false,
      addCardTransactionReff: "",
      webResponseLoc: "",
      status:"Active"
    });

    // Reset dialog visibility and message properties
    Object.assign(addCardDialogMessage.value, {
      isVisible: false,
      spinner: false,
      message: "",
      color: "",
    });
    isAddCardDialogVisible.value = false;
  } catch (error) {
    console.log("Error closing card add dialog,", error);
  }
};

const verifyMobile = ref(false)
const userMobileNumber = ref('')
const userMobileId = ref();
const mobile2FACode = ref('');
const hasRequestedSMS = ref(false)

const LocalMobileValidator = (value: any) => {
  if (isEmpty(value)) return 'You must provide a valid Mobile Number'

  value = value.replace(/\D/g,'');
  const re = /^([0|\+[0-9]{1,5})?([0-9]{10})$/;
  if (Array.isArray(value))
    return (
      value.every((val) => re.test(String(val))) ||
      "This must be a valid mobile number"
    );
  return re.test(String(value)) || "This must be a valid mobile number";
};


const loadingMobileVerify = ref(false)
const smsValidateError = ref(false)
const handleAddMobile = async() => {
  loadingMobileVerify.value = true

  let tempData = {
    mobileNumber: userMobileNumber.value
  }
  try {
    await pb.collection("users").update(userMobileId.value, tempData);
  } catch(e){}
  verifyMobile.value = false
  loadingMobileVerify.value = false
  await router.push("/");
}

const VerifySMSMobile = async() => {
  loadingMobileVerify.value = true

  let tempData = {
    mobileNumber: userMobileNumber.value,
    status: 'Active',
    mobileValidated: true,
    twoFACode: ''
  }
  let isValid = false;
  smsValidateError.value = false;

  try {
    let userCheck = await pb.collection("users").getFirstListItem('id="' + userMobileId.value + '" && twoFACode="' + mobile2FACode.value + '"')
    if (userCheck) {
      isValid = true
      await pb.collection("users").update(userMobileId.value, tempData);
    }
  } catch (e) {}

  loadingMobileVerify.value = false

  if (isValid) {
    verifyMobile.value = false
    await router.push("/");
  } else {
    smsValidateError.value = true
  }
}

const sendSMS = async() =>{
  loadingMobileVerify.value = true
  try{
    let tempData = {
      twoFaCode: '',
      status:'ValidatingMobile'
    }
    await pb.collection("users").update(userMobileId.value, tempData);
    hasRequestedSMS.value = true
  }catch(e){}
  loadingMobileVerify.value = false
}
</script>

<template>
  <div>
    <div class="auth-wrapper d-flex align-center justify-center pa-4">
      <!-- error dialog for suspended account -->
      <VDialog v-model="isErrorPopupVisible" width="auto" persistent>
        <VCard max-width="450" max-height="500" height="180">
          <VCardText class="text-h6">
            There is an issue with your account please contact us to rectify
          </VCardText>
          <template v-slot:actions>
            <VBtn
              class="ms-auto"
              text="Close"
              @click="isErrorPopupVisible = false"
            ></VBtn>
          </template>
        </VCard>
      </VDialog>

      <VCard class="auth-card pa-2 pt-7" max-width="500">
        <VCardItem class="justify-center">
          <img
            v-if="getSignInLogo"
            :src="getSignInLogo"
            alt="Site Logo"
            class="center logoImage auth-card__logo"
          />
        </VCardItem>

        <VCardText class="pt-2 auth-card__static-text">
          <p class="mb-0">Please sign-in to your account</p>
        </VCardText>

        <VCardText>
          <VForm ref="form" @submit.prevent="onSubmit">
            <VRow>
              <!-- email -->
              <VCol cols="12">
                <VTextField
                  v-model="email"
                  label="Username or Email"
                  autofocus
                  :rules="[requiredValidator]"
                  :error-messages="errors.email"
                />
              </VCol>

              <!-- password -->
              <VCol cols="12">
                <VTextField
                  v-model="password"
                  label="Password"
                  :rules="[password.length >= 8? true : 'Password must be 8 characters or more']"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :append-inner-icon="
                    isPasswordVisible
                      ? 'mdi-eye-outline'
                      : 'mdi-eye-off-outline'
                  "
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />

                <!-- remember me checkbox -->
                <div
                  class="d-flex align-center justify-space-between flex-wrap mt-1 mb-4"
                >
                  <VCheckbox v-model="rememberMe" label="Remember me" />

                  <RouterLink
                    class="text-primary ms-2 mb-1"
                    :to="{ name: 'forgot-password' }"
                  >
                    Forgot Password?
                  </RouterLink>
                </div>

                <!-- login button -->
                <VBtn block type="submit" :loading="loginLoading"> Login </VBtn>
              </VCol>

              <!-- create account -->
              <VCol
                cols="12"
                class="text-center text-base auth-card__static-text"
              >
                <span>New on our platform?</span>
                <RouterLink
                  class="text-primary ms-2"
                  :to="{ name: 'register' }"
                >
                  <span @click="clearLocalStorage">Create an account</span>
                </RouterLink>
              </VCol>

              <!-- auth providers -->
              <VCol cols="12" class="text-center auth-card__social-login">
                <AuthProvider @clicked="handleSocialAuth" />
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
        <VCardItem>
          <a href="https://www.BayBooker.com">
            <img
              width="180"
              height="33"
              src="@images/FullLogo180_33.png"
              alt="www.BayBooker.com"
              class="center"
            />
          </a>
        </VCardItem>
      </VCard>
      <VImg
        :src="getBackGroundImage"
        :alt="authV1ThemeLoginMask"
        class="auth-background-image"
      />
    </div>
    <TwoFactorAuthDialog
      :email="email"
      :password="password"
      :is-dialog-open="isTwoFADialogOpen"
      @close-dialog="isTwoFADialogOpen = false"
    />

    <ImportedTwoFactorAuthDialog
      :email="email"
      :password="password"
      :importedID="importedID"
      :is-dialog-open="isImportedTwoFADialogOpen"
      @close-dialog="isImportedTwoFADialogOpen = false"
    />
    <OAuthSignupDialog
      :is-drawer-open="isOAuthSignupDialog"
      :auth-user="userToAuth"
      @set-auth-user="handleOAuthSignUp"
      @close-dialog="isOAuthSignupDialog = false"
    />
  </div>

  <VDialog v-model="verifyMobile" max-width="400" persistent>
    <VCard>
      <VCardTitle class="text-center">Verify Mobile Number</VCardTitle>
      <VCardText class="py-2">
        <div class="mb-5">We need to verify your Mobile number as part of your membership.</div>

        <VTextField
          v-model="userMobileNumber"
          label="Mobile Number"
          :rules="[LocalMobileValidator]"
          placeholder="Mobile number"
          @keyup="userMobileNumber = userMobileNumber.replace(/\D/g,'')"
        />

        <p class="mt-5 text-end" v-if="hasRequestedSMS">
          Did not receive a code?
          <span
            class="text-primary cursor-pointer text-decoration-underline"
            @click="sendSMS()">Resend</span>
        </p>

        <div v-if="globalSetup.validateMobileNumbers">
          <div class="text-end" v-if="!hasRequestedSMS">
            <VBtn class="ms-auto mt-2 mb-3" @click.prevent="sendSMS()" :disabled="!userMobileNumber || userMobileNumber.length < 7">Send SMS Code</VBtn>
          </div>
        <VTextField
          v-if="hasRequestedSMS"
          v-model="mobile2FACode"
          label="SMS Code"
          :error-messages="smsValidateError ? 'That Code Does not match!' : true"
          placeholder="SMS Code"
        />
        </div>
      </VCardText>
      <VDivider class="py-2" />

      <VCardActions>
        <VSpacer></VSpacer>
        <VBtn v-if="!globalSetup.validateMobileNumbers" variant="flat" @click="handleAddMobile()" :loading="loadingMobileVerify" :disabled="!userMobileNumber || userMobileNumber.length < 7"> Save Mobile Number </VBtn>
        <VBtn v-else variant="flat" @click="VerifySMSMobile()" :loading="loadingMobileVerify" :disabled="!userMobileNumber || userMobileNumber.length < 7 || !mobile2FACode || mobile2FACode.length < 3 || !hasRequestedSMS">Validate</VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <VDialog v-model="verifyCC" max-width="400" persistent>
    <VCard>
      <VCardTitle class="text-center">Welcome to our new platform</VCardTitle>
      <VCardText class="py-2">Credit card details could not be imported, please enter your card details.</VCardText>
      <VDivider class="py-2" />

      <VCardActions>
        <VSpacer />
        <VBtn variant="flat" @click="handleAddCard()"> Add Card </VBtn>
        <VBtn variant="flat" v-if="directDebitEnabled"> Add Direct Debit </VBtn>
        <span v-else style="width:150px;">&nbsp;</span>
        <span class="ps-7 pr-6"></span>
        <VBtn variant="flat" color="secondary" @click="closeVerifyCC()"> Close </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <VDialog v-model="oldLogin" max-width="550" persistent v-if="getOldLoginLink() != ''">
    <VCard>
      <VCardTitle class="text-center">Welcome to our new platform</VCardTitle>
      <VCardText class="py-2">Your login could still be with a former system,<br>would you like to login there instead?</VCardText>
      <VDivider class="py-2" />
      <VCardActions>
        <VSpacer />
        <VBtn variant="flat" link :href="getOldLoginLink()" class=""> Old Booking URL </VBtn>
        <VBtn variant="text" link to="/register" class=""> Create <span class="d-none d-sm-inline ms-2 me-2">New</span> Account</VBtn>
        <span class="ps-12 pe-13 d-none d-sm-block"></span>
        <VBtn variant="flat" color="secondary" class="d-none d-sm-block" @click="oldLogin = false"> Close </VBtn>
      </VCardActions>
      <VCardActions class="d-block d-sm-none text-end" >
        <VBtn variant="flat" color="secondary" @click="oldLogin = false"> Close </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- 👉 Add Card Dialog -->
  <VDialog
    :model-value="isAddCardDialogVisible"
    persistent
    max-width="600"
    max-height="800"
    no-click-animation
    @update:model-value="closeAddCardDialog"
  >
    <VCard>
      <div
        class="d-flex align-center justify-space-between bg-dialogHeaderColor"
      >
        <VCardTitle> Add Card </VCardTitle>

        <VBtn
          icon="mdi-close"
          size="large"
          variant="text"
          class="rounded-0"
          color=""
          @click="isAddCardDialogVisible = false"
        />
      </div>

      <VCardText class="text-center">
        <VProgressCircular
          v-if="addCardDialogMessage.spinner && addCardDialogMessage.isVisible"
          :color="addCardDialogMessage.color"
          indeterminate
          :size="50"
          variant="outlined"
        />
        <p
          class="text-h5 my-5"
          :class="{
            'text-error': addCardDialogMessage.color === 'error',
            'text-success': addCardDialogMessage.color === 'success',
          }"
        >
          {{ addCardDialogMessage.message }}
        </p>
      </VCardText>

      <VCardActions>
        <VSpacer />
        <VBtn color="error" variant="flat" @click="closeAddCardDialog">
          Close
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>


  <VDialog v-model="transitionMembers" max-width="550" persistent>
    <VCard>
      <VCardTitle class="text-center border-b">Switching Booking Platform</VCardTitle>
      <VCardText class="py-2">
        <h4 class="pb-5">We are in the Process of switching to our new booking platform</h4>
        <p>We will be moving members over in batches, If you have been advised you have been moved to the BayBooker platform, then please logon using the BayBooker logon, otherwise please logon using the your old {{globalSetup.oldMemberPlatformName}} logon.</p>
      </VCardText>
      <VDivider class="py-2" />
      <VCardActions>
        <VBtn variant="flat" class="ps-3 pe-3" @click="transitionMembers = false"> BayBooker Logon </VBtn>
        <VSpacer />
        <VBtn variant="flat" color="secondary" class="ps-3 pe-3" :href="globalSetup?.oldMemberPlatformUrl"> {{globalSetup?.oldMemberPlatformName}} Logon </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

</template>

<style lang="scss" scoped>
@use "@core/scss/template/pages/page-auth.scss";

.logoImage {
  block-size: 294px;
  inline-size: 400px;
  object-fit: contain;

  @media (max-width: 500px) {
    block-size: auto;
    inline-size: 100%;
  }
}
</style>

<route lang="yaml">
meta:
  layout: blank
</route>
